<template>
    <div>
        <jet-form-card @submitted="submit">
            <template #form>
                <div class="col-span-6">
                    <jet-label for="title" value="Title"/>
                    <jet-input id="title" type="text" class="mt-1 block w-full" v-model="form.title" required
                               :error="!!form.errors.title"
                               autofocus/>
                    <jet-input-error :message="form.errors.title"/>
                </div>
                <div class="col-span-6">
                    <jet-label for="pageType" value="Page Type"/>
                    <jet-select id="pageType"
                                v-model:selected="form.page_type_id"
                                :disabled="page?.page_type_id === 1"
                                :error="!!form.errors.page_type_id">
                        <option :value="null" disabled>Select page type</option>
                        <option v-for="(pageType, value) in pageTypes" :value="value">{{ pageType }}</option>
                    </jet-select>
                    <jet-input-error :message="form.errors.page_type_id"/>
                </div>
                <div class="col-span-6">
                    <jet-label for="thumbnail_button_label" value="Thumbnail Button Label"/>
                    <jet-input id="thumbnail_button_label" type="text" class="mt-1 block w-full" v-model="form.thumbnail_button_label" required
                               :error="!!form.errors.title"
                               autofocus/>
                    <jet-input-error :message="form.errors.title"/>
                </div>
                <div class="col-span-6">
                    <input type="file"
                           class="hidden"
                           ref="photoInput"
                           accept="image/*"
                           @change="updatePhotoPreview">

                    <jet-label for="image" value="Image"/>

                    <div class="mt-2" v-if="photoPreview">
                        <img :src="photoPreview" :alt="page?.title" class="sm:rounded-md h-80 w-160 object-cover">
                    </div>

                    <jet-secondary-button class="mt-2 mr-2" type="button" @click.prevent="selectNewPhoto">
                        Select A New Image
                    </jet-secondary-button>

                    <jet-secondary-button type="button" class="mt-2"
                                          v-if="photoPreview" @click.prevent="deletePhoto">
                        Remove Image
                    </jet-secondary-button>

                    <jet-input-error :message="form.errors.image" class="mt-2"/>
                </div>
                <div class="col-span-6">
                    <jet-label for="title" value="Content"/>
                    <ckeditor :editor="editor" v-model="form.html" :config="editorConfig"></ckeditor>
                    <jet-input-error :message="form.errors.html"/>
                </div>

                <div class="col-span-6">
                    <jet-label for="active" value="Status"/>
                    <jet-select id="active" v-model:selected="form.status" :error="!!form.errors.status">
                        <option v-for="(status, value) in pageStatuses" :value="value">{{ status }}</option>
                    </jet-select>
                    <jet-input-error :message="form.errors.status"/>
                </div>
            </template>
            <template #actions>
                <jet-action-message :on="form.recentlySuccessful" class="mr-3">
                    Saved.
                </jet-action-message>
                <jet-button :class="{ 'opacity-25': form.processing }"
                            :disabled="form.processing">
                    Save
                </jet-button>
            </template>
        </jet-form-card>
    </div>
</template>

<script>
import JetInput from '@/Jetstream/Input';
import JetButton from '@/Jetstream/Button';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import JetLabel from '@/Jetstream/Label';
import JetSelect from '@/Jetstream/Select';
import JetFormCard from '@/Jetstream/FormCard';
import JetActionMessage from '@/Jetstream/ActionMessage.vue'
import JetInputError from '@/Jetstream/InputError';
import {useForm, usePage} from "@inertiajs/inertia-vue3";
import CKEditor from '@ckeditor/ckeditor5-vue';
import CustomEditor from "@/includes/ck-editor-custom";
import {ref} from "vue";

export default {
    name: "PageEditor",
    components: {
        JetInput,
        JetLabel,
        JetSelect,
        JetButton,
        JetSecondaryButton,
        JetFormCard,
        JetActionMessage,
        JetInputError,
        ckeditor: CKEditor.component
    },
    props: {
        client: {
            type: Object,
            required: true,
        },
        project: {
            type: Object,
            required: true,
        },
        pageStatuses: {
            type: Array,
            required: true,
        },
        page: {
            type: Object,
            required: false,
        },
        pageTypes: {
            type: Object,
            required: true,
        }
    },
    setup(props) {
        const formData = {
            title: props.page?.title ?? '',
            page_type_id: props.page?.page_type_id ?? null,
            thumbnail_button_label: props.page?.thumbnail_button_label ?? 'View page',
            thumbnail_image: props.page?.thumbnail_image ?? null,
            html: props.page?.html ?? '',
            status: props.page?.status ?? 0,
        };

        let form = null;
        if (!props.page) {
            form = useForm(formData);
        } else {
            form = useForm({
                _method: 'PATCH',
                ...formData,
            })
        }

        let photo = props.page?.thumbnail_image;
        const photoPreview = ref(props.page?.thumbnail_image);
        const photoInput = ref(null);

        const reader = new FileReader();
        reader.onload = (e) => {
            photoPreview.value = e.target.result;
        };

        const selectNewPhoto = () => {
            photoInput.value.click();
        };

        const updatePhotoPreview = (event) => {
            photo = event.target.files[0];

            if (!photo) {
                return;
            }

            reader.readAsDataURL(photo);
        };

        const deletePhoto = () => {
            photo = null;
            photoPreview.value = null;
        };

        const submit = () => {
            if (photo === null || photo instanceof File) {
                form.thumbnail_image = photo;
            } else {
                delete form.thumbnail_image;
            }

            if (!props.page) {
                form.post(route('admin.page.store', {client: props.client.id, project: props.project.id}));
            } else {
                form.post(route('admin.page.update', {client: props.client.id, project: props.project.id, page: props.page.id}));
            }
        };

        const editor = CustomEditor;
        const editorConfig = {
            simpleUpload: {
                uploadUrl: route('admin.client.image.upload', {client: props.client.id}),
                headers: {
                    'X-CSRF-TOKEN': usePage().props.value.csrf_token,
                }
            }
        };

        return {
            editor,
            editorConfig,
            form,
            photoInput,
            photoPreview,
            selectNewPhoto,
            updatePhotoPreview,
            deletePhoto,
            submit
        };
    }
}
</script>

<style scoped>

</style>
